/**
 * Last-5 (style, colorName) picks stored per-browser. Shown beside
 * Quick picks when the Style combobox is empty. Persistence is
 * client-only — no backend state, no cross-device sync. Matches the
 * Phase 4a choice to keep ops state lightweight.
 */

const KEY = 'fsp.ss.recents.v1';
const MAX = 5;

export type Recent = {
  style: string;
  colorName: string;
};

export function loadRecents(): Recent[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (v): v is Recent =>
          !!v &&
          typeof v === 'object' &&
          typeof (v as Recent).style === 'string' &&
          typeof (v as Recent).colorName === 'string',
      )
      .slice(0, MAX);
  } catch {
    return [];
  }
}

/**
 * Prepend a new pick. Dedupes by case-insensitive (style, color)
 * equality — re-quoting the same thing shuffles it to the front
 * instead of duplicating. Caps at MAX entries.
 */
export function pushRecent(entry: Recent): Recent[] {
  const existing = loadRecents();
  const key = recentKey(entry);
  const filtered = existing.filter((r) => recentKey(r) !== key);
  const next = [entry, ...filtered].slice(0, MAX);
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode / quota). Best-effort; the
    // picker still works without persistence.
  }
  return next;
}

function recentKey(r: Recent): string {
  return `${r.style.trim().toLowerCase()}|${r.colorName.trim().toLowerCase()}`;
}
